/**
 * Glossary Page - Emotional Vocabulary & Nature Words
 * 
 * Displays an alphabetical list of words from "Where the Brave Things Grow"
 * with child-friendly definitions.
 * 
 * Features:
 * - Alphabetical organization (A-Z sections)
 * - Category badges (Emotion/Nature)
 * - Search functionality
 * - Filter by category
 * - Color-coded by category (emotions = blue, nature = green)
 */

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { glossaryData, getAlphabeticalGroups, searchGlossary } from "../lib/glossary-data.ts";
import { BookOpen, Search, Heart, Leaf, ArrowLeft } from "lucide-react";
import { motion } from "motion/react";

export function Glossary() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'emotion' | 'nature'>('all');

  // Filter and search logic
  const filteredData = useMemo(() => {
    let data = glossaryData;

    // Apply category filter
    if (categoryFilter !== 'all') {
      data = data.filter(entry => entry.category === categoryFilter);
    }

    // Apply search filter
    if (searchTerm.trim() !== '') {
      data = searchGlossary(searchTerm);
      if (categoryFilter !== 'all') {
        data = data.filter(entry => entry.category === categoryFilter);
      }
    }

    return data;
  }, [searchTerm, categoryFilter]);

  // Group filtered data alphabetically
  const alphabetGroups = useMemo(() => {
    const groups: Record<string, typeof filteredData> = {};
    
    filteredData.forEach(entry => {
      const firstLetter = entry.word[0].toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(entry);
    });
    
    return groups;
  }, [filteredData]);

  const sortedLetters = Object.keys(alphabetGroups).sort();

  return (
    <div className="min-h-screen bg-gradient-to-br from-golden-50 via-cream-50 to-forest-50">
      {/* HEADER */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-forest-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/book/wtbtg')}
              variant="ghost"
              size="sm"
              className="text-forest-700 hover:bg-transparent hover:text-forest-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Contents
            </Button>
            <div className="text-forest-400">•</div>
            <div>
              <h1 className="text-2xl font-bold text-forest-800">Glossary</h1>
              <p className="text-sm text-forest-600">Words to help you understand the story</p>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* SEARCH & FILTER BAR */}
        <Card className="mb-8 border-2 border-forest-200">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-forest-400" />
                <Input
                  type="text"
                  placeholder="Search for a word..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-forest-300 focus:ring-forest-500 text-base"
                />
              </div>

              {/* Category Filter Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => setCategoryFilter('all')}
                  variant={categoryFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className={categoryFilter === 'all' ? 'bg-forest-600 hover:bg-forest-700' : 'border-forest-300'}
                >
                  All Words
                </Button>
                <Button
                  onClick={() => setCategoryFilter('emotion')}
                  variant={categoryFilter === 'emotion' ? 'default' : 'outline'}
                  size="sm"
                  className={categoryFilter === 'emotion' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-300 text-blue-700'}
                >
                  <Heart className="w-4 h-4 mr-1" />
                  Emotions
                </Button>
                <Button
                  onClick={() => setCategoryFilter('nature')}
                  variant={categoryFilter === 'nature' ? 'default' : 'outline'}
                  size="sm"
                  className={categoryFilter === 'nature' ? 'bg-green-600 hover:bg-green-700' : 'border-green-300 text-green-700'}
                >
                  <Leaf className="w-4 h-4 mr-1" />
                  Nature
                </Button>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-forest-600">
              Showing <span className="font-semibold">{filteredData.length}</span> word{filteredData.length !== 1 ? 's' : ''}
              {searchTerm && ` matching "${searchTerm}"`}
              {categoryFilter !== 'all' && ` in ${categoryFilter === 'emotion' ? 'Emotions' : 'Nature'}`}
            </div>
          </CardContent>
        </Card>

        {/* ALPHABETICAL WORD LIST */}
        {sortedLetters.length === 0 ? (
          /* NO RESULTS STATE */
          <Card className="border-2 border-forest-200">
            <CardContent className="p-12 text-center">
              <p className="text-lg text-forest-600 mb-2">No words found</p>
              <p className="text-sm text-forest-500">Try a different search term or filter</p>
            </CardContent>
          </Card>
        ) : (
          /* WORD GROUPS */
          <div className="space-y-8">
            {sortedLetters.map((letter) => (
              <motion.div
                key={letter}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Letter Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-forest-600 to-golden-600 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{letter}</span>
                  </div>
                  <div className="flex-1 h-px bg-forest-200"></div>
                </div>

                {/* Words in This Letter Group */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {alphabetGroups[letter].map((entry, index) => (
                    <motion.div
                      key={`${entry.word}-${index}`}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <Card className={
                        entry.category === 'emotion' 
                          ? 'border-l-4 border-l-blue-500 hover:shadow-md transition-shadow'
                          : 'border-l-4 border-l-green-500 hover:shadow-md transition-shadow'
                      }>
                        <CardContent className="p-4">
                          {/* Word + Category Badge */}
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-xl font-bold text-forest-800">{entry.word}</h3>
                            <Badge 
                              className={
                                entry.category === 'emotion'
                                  ? 'bg-blue-100 text-blue-800 border-blue-300'
                                  : 'bg-green-100 text-green-800 border-green-300'
                              }
                            >
                              {entry.category === 'emotion' ? (
                                <><Heart className="w-3 h-3 mr-1" /> Emotion</>
                              ) : (
                                <><Leaf className="w-3 h-3 mr-1" /> Nature</>
                              )}
                            </Badge>
                          </div>

                          {/* Definition */}
                          <p className="text-base text-forest-700 leading-relaxed mb-3">
                            {entry.definition}
                          </p>

                          {/* Example (if available) */}
                          {entry.example && (
                            <div className="bg-golden-50 border-l-2 border-golden-400 pl-3 py-2 rounded-r">
                              <p className="text-sm italic text-forest-600">
                                <span className="font-semibold not-italic text-golden-700">Example:</span>{' '}
                                {entry.example}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* FOOTER INFO */}
        <Card className="mt-12 border-2 border-forest-200 bg-cream-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <BookOpen className="w-6 h-6 text-forest-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-forest-800 mb-2">About This Glossary</h3>
                <p className="text-forest-700 leading-relaxed">
                  This glossary contains <span className="font-semibold">{glossaryData.filter(e => e.category === 'emotion').length} emotional vocabulary words</span> and{' '}
                  <span className="font-semibold">{glossaryData.filter(e => e.category === 'nature').length} nature words</span> from "Where the Brave Things Grow."
                  These definitions are written in simple language to help young readers understand the story better.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
